import { useEffect, useState } from "react";
import { createContainer } from "unstated-next";
import { VOICIFY_SPARK_DEPLOYMENT_STATE } from "../constants/Keys";
import * as voicifyApi from '../api';
import ApplicationEnvironmentModel from "../models/applications/api/environments/ApplicationEnvironmentModel";
import { MultipleTemplateSyncsStatus } from "../state/containers/AppliedApplicationTemplateContainer";

export type TrainingState = "training" | "trained" | "error";

interface SparkDeploymentState {
    [appId: string]: {
        appliedTemplateSyncId?: string,
        orgId: string,
        syncStage?: "Processing" | "Queued" | "Complete" | "Error",
        /** Unix ms timestamp of when this entry was last updated. Used to evict stale entries on mount. */
        timestamp?: number,
    }
}

function useSparkDeploymentContainer() {

    const [currentOrgId, setCurrentOrgId] = useState<string>("");
    const [deploymentState, setDeploymentState] = useState<SparkDeploymentState>(null);
    const [environments, setEnvironments] = useState<ApplicationEnvironmentModel[]>([]);
    const [pollingTrigger, setPollingTrigger] = useState(false);
    const [polling, setPolling] = useState(false);

    useEffect(() => {
        // Load state from localStorage but DON'T start polling automatically
        // Polling should only start when pollForApplication is explicitly called.
        // Also evict any entries older than 24 hours to prevent stale sync IDs
        // from being picked up and firing 404 GET requests on the next session.
        const stored = getStoredState();
        const MAX_AGE_MS = 24 * 60 * 60 * 1000;
        const now = Date.now();
        const cleaned: SparkDeploymentState = {};
        Object.entries(stored).forEach(([appId, entry]) => {
            if (!entry.timestamp || now - entry.timestamp < MAX_AGE_MS) {
                cleaned[appId] = entry;
            }
        });
        setDeploymentState(cleaned);
    }, []);

    useEffect(() => {
        if (!polling) return;
        pollForUnfinishedDeployments();
    }, [pollingTrigger, polling]);

    const getStoredState = (): SparkDeploymentState => {
        let storedState: SparkDeploymentState = {};
        const storedTrainingState = localStorage.getItem(VOICIFY_SPARK_DEPLOYMENT_STATE);
        if (storedTrainingState)
            storedState = JSON.parse(storedTrainingState);
        return storedState;
    };

    const storeState = (state: SparkDeploymentState) => {
        const json = JSON.stringify(state);
        localStorage.setItem(VOICIFY_SPARK_DEPLOYMENT_STATE, json);
    };

    const pollForApplication = async (appId: string, orgId: string, appliedTemplateSyncId?: string) => {
        const newState = { ...deploymentState };

        // Only track apps that have an active sync
        if (!appliedTemplateSyncId) {
            return;
        }

        if (appId in newState) {
            // App already being tracked, update with new sync ID and reset to Queued
            newState[appId].appliedTemplateSyncId = appliedTemplateSyncId;
            newState[appId].syncStage = "Queued"; // New sync started
            newState[appId].timestamp = Date.now();
        } else {
            // New app to track
            newState[appId] = {
                appliedTemplateSyncId: appliedTemplateSyncId,
                orgId: orgId,
                syncStage: "Queued",
                timestamp: Date.now(),
            }
        }
        setDeploymentState(newState);
        storeState(newState);

        // Start polling when explicitly requested
        if (!polling) {
            setPolling(true);
            setPollingTrigger(!pollingTrigger);
        }
    };

    const getAppDeploymentState = (
        appId: string,
        containerState?: {
            isWaitingForTemplateSync?: boolean;
            multipleTemplateSyncsStatus?: MultipleTemplateSyncsStatus;
        }
    ): TrainingState => {
        // First check container state if provided (matches AppliedAppTemplateReviewForm getSubmissionState pattern)
        if (containerState) {
            if (containerState.isWaitingForTemplateSync) {
                return "training";
            }
            if (containerState.multipleTemplateSyncsStatus === "syncing") {
                return "training";
            }
            if (containerState.multipleTemplateSyncsStatus === "error") {
                return "error";
            }
            if (containerState.multipleTemplateSyncsStatus === "success") {
                return "trained";
            }
        }

        // Check deployment state tracking from polling
        if (!deploymentState || !(appId in deploymentState))
            return "trained";

        const appState = deploymentState[appId];

        // If no sync ID is being tracked, return trained (no active sync)
        if (!appState.appliedTemplateSyncId) {
            return "trained";
        }

        // Check sync stage from polling (matches AppliedAppTemplateReviewForm pattern)
        if (appState.syncStage === "Processing" || appState.syncStage === "Queued") {
            return "training";
        }
        if (appState.syncStage === "Error") {
            return "error";
        }
        if (appState.syncStage === "Complete") {
            return "trained";
        }

        // Default to trained if no sync stage is set yet
        return "trained";
    };

    const getAppIdsToPoll = (): string[] => {
        if (!deploymentState)
            return [];
        const appIds = Object.keys(deploymentState);
        return appIds.filter(appId => {
            const st = getAppDeploymentState(appId);
            if (st === "training")
                return true;
            return false;
        });
    };

    const pollForUnfinishedDeployments = async () => {
        const newDeploymentState = { ...deploymentState };
        const appIds = getAppIdsToPoll();

        // If no apps are in training state, stop polling
        if (appIds.length === 0) {
            setPolling(false);
            return;
        }

        let env: ApplicationEnvironmentModel = null;
        const newEnvs = [...environments];
        for (const appId of appIds) {
            const appliedTemplateSyncId = newDeploymentState[appId].appliedTemplateSyncId;
            // find env for that app
            env = newEnvs.find(e => e.applicationId === appId && !e.isDraft && !e.isCustom);
            if (!env) {
                const environmentResults = await voicifyApi.getApplicationEnvironments(appId);
                if (environmentResults.resultType == "Ok") {
                    newEnvs.push(...environmentResults.data);
                    env = environmentResults.data.find(e => e.applicationId === appId && !e.isDraft && !e.isCustom);
                }
            }
            if (!env) {
                // something is wrong, we can't find the environment
                continue;
            }
            if (appliedTemplateSyncId) {
                const result = await voicifyApi.getAppliedTemplateSync(appliedTemplateSyncId);
                if (result.resultType == "Ok") {
                    const sync = result.data;
                    // Store the sync stage to match AppliedAppTemplateReviewForm behavior
                    newDeploymentState[appId].syncStage = sync.stage as "Processing" | "Queued" | "Complete" | "Error";

                    // Clean up completed or errored syncs - clear the sync ID so they stop being tracked
                    if (sync.stage === "Complete" || sync.stage === "Error") {
                        newDeploymentState[appId].appliedTemplateSyncId = undefined;
                    }
                } else {
                    // The sync ID is no longer found on the server (e.g. 404).
                    // Clear it immediately so we stop polling a stale/invalid ID.
                    newDeploymentState[appId].appliedTemplateSyncId = undefined;
                    newDeploymentState[appId].syncStage = undefined;
                }
            } else {
                const publishesResult = await voicifyApi.getEnvironmentPublishes(env.id, 0, 50);
                if (publishesResult.resultType == "Ok") {
                    const publishes = publishesResult.data;
                    // get last publish that is complete
                    const lastPublish = publishes.find(p => p.stage === "Complete");
                    if (lastPublish) {
                        // Mark as complete
                        newDeploymentState[appId].syncStage = "Complete";
                    }
                }
            }

        }
        setDeploymentState(newDeploymentState);
        setEnvironments(newEnvs);
        storeState(newDeploymentState);

        // Check whether any apps are still in training state before scheduling the next poll.
        // We recompute from newDeploymentState directly (rather than relying on the stale
        // `deploymentState` closure) to avoid a race where setPolling(false) hasn't committed
        // yet but the useEffect fires again due to the trigger flip.
        const stillTraining = Object.keys(newDeploymentState).some(appId => {
            const st = newDeploymentState[appId];
            return !!st.appliedTemplateSyncId && (st.syncStage === "Processing" || st.syncStage === "Queued");
        });

        if (!stillTraining) {
            setPolling(false);
            return;
        }

        // Continue polling — wait then flip the trigger so the useEffect fires again
        await new Promise(r => setTimeout(r, 7500));
        setPollingTrigger(prev => !prev);
    };

    return {
        setCurrentOrgId,
        pollForApplication,
        getAppDeploymentState,
        deploymentState,
    }

};

const SparkDeploymentContainer = createContainer(useSparkDeploymentContainer);
export default SparkDeploymentContainer;
